import { DashboardProductStates } from "config";
import { RootState } from "store";

import { createSelector } from "@reduxjs/toolkit";

import { getInsuranceMembership } from "./selectMemberships";

const selectEstimateId = (state: RootState) => state.estimate?.id;
const selectPolicy = (state: RootState) => state.policy;
const selectProductOfferings = (state: RootState) => state.productOffering;
const selectInsuranceApplication = (state: RootState) => state.insuranceApplication;
const selectAllocationSummary = (state: RootState) => state.allocationSummary;
const selectWyshAllocations = (state: RootState) => state.wyshAllocations.allocated;

/**
 * Determines the current insurance product state on Home/Dashboard screen
 *
 * The returned state is determined in the following priority order:
 * - `"owned"`: the user has an active policy
 * - `"notice"`: the insurance application is in manual underwriting review (i.e., requires manual review by an underwriter)
 * - `"quote"`: there are available product offerings
 * - `"reviewing"`: the insurance application is submitted and is pending automated review/processing (distinct from `"notice"`, which is for manual underwriting)
 * - `"recapture"`: an estimate exists
 * - `"marketing"`: Default state if none of the above conditions are met.
 */
export const getInsuranceProductState = createSelector(
  getInsuranceMembership,
  selectPolicy,
  selectInsuranceApplication,
  selectEstimateId,
  selectProductOfferings,
  (
    insuranceMembership,
    activePolicy,
    insuranceApplication,
    estimateId,
    productOfferings
  ): DashboardProductStates => {
    if (activePolicy.id) {
      return "owned";
    } else if (insuranceApplication.result === "referred_to_underwriter") {
      return "notice";
    } else if (productOfferings.ids.length) {
      return "quote";
    } else if (
      insuranceApplication.result === "pending" &&
      !insuranceMembership.eligibleForUnderwriting
    ) {
      return "reviewing";
    } else if (estimateId) {
      return "recapture";
    } else {
      return "marketing";
    }
  }
);

export const getWyshesProductState = createSelector(
  selectWyshAllocations,
  selectAllocationSummary,
  (wyshAllocations, allocationSummary): Extract<DashboardProductStates, "marketing" | "owned"> => {
    if (wyshAllocations.length || allocationSummary.activeCount) {
      return "owned";
    } else {
      return "marketing";
    }
  }
);
